import { getArboritoStore } from '../core/store-singleton.js';
import { notifyIdentityChanged } from './store-notify.js';
import { DEMO_BRANCH_ID } from '../core/demo/arborito-demo-ids.js';
import {
    ensureLocalEd25519Identity,
    buildSignedIdentityClaim,
    verifyIdentityClaimRecord,
} from '../features/identity-auth/api/arborito-identity.js';
import {
    hasGdprNetworkConsent,
    onGdprNetworkConsentGranted,
    getConnectedNostr,
    warmNostrRelayConnections,
} from '../shared/lib/connected-services/index.js';
import { CREDENTIAL_KIND_PASSWORD, resolveSessionCredentialKind } from '../features/identity-auth/api/sync-login-secret.js';
import { buildRecoveryKitQrPayload, ensureRecoveryKeyInSession } from '../features/identity-auth/api/recovery-kit.js';
import { qrTextToDataUrl } from '../features/identity-auth/api/identity-qr.js';
import {
    loadPersistedAuthSession,
    clearPersistedAuthSession,
    persistAuthSession,
} from '../features/identity-auth/api/auth-session-persist.js';
import { findCommunitySourceByUrl } from '../features/sources/api/modals/logic/sources-helpers.js';
import { isSourcesWelcomeLoadClose } from '../features/sources/api/sources-session.js';
import { loadDataAction } from './sources-store-actions.js';
import { dismissModalAction } from './shell-ui-store-actions.js';

function shell() {
    return getArboritoStore();
}

function canonicalTreeKey(url) {
    const u = String(url || '').trim();
    if (u.startsWith('privtree://')) {
        return `branch://${u.slice('privtree://'.length).split('/')[0]}`;
    }
    if (u.startsWith('tree://')) {
        return `tree://${u.slice('tree://'.length).split('/')[0]}`;
    }
    if (u.startsWith('branch://')) {
        return `branch://${u.slice('branch://'.length).split('/')[0]}`;
    }
    return u;
}

export function isSignedInAction() {
    const store = shell();
    if (!store) return undefined;
    return !!(store._authSession && store._authSession.username);
}

export function isSyncAccountAction() {
    const store = shell();
    if (!store) return undefined;
    const s = store._authSession;
    return !!(s && s.username && s.authMode === 'sync');
}

export function signOutAction() {
    const store = shell();
    if (!store) return undefined;

    store._authSession = null;
    clearPersistedAuthSession();
    try {
        cancelAutoloadTreeAfterSignInAction();
    } catch {
        /* ignore */
    }
    try {
        store._restoredActiveSourceUrl = '';
        store._userChoseActiveSource = false;
        store._autoloadMountInFlight = false;
        store._autoloadAfterSignInPending = false;
    } catch {
        /* ignore */
    }
    try {
        store.cancelPendingAccountSyncTimers?.();
    } catch {
        /* ignore */
    }
    try {
        const n = store.userStore?.clearAllPrivateSyncedFromAccountFlags?.() || 0;
        if (n) {
            store.sourceManager?.refreshPrivateAccountSources?.();
        }
    } catch {
        /* ignore */
    }
    notifyIdentityChanged(store);
}

/** Abort in-flight post–sign-in autoload (user already picked a tree). */
export function cancelAutoloadTreeAfterSignInAction(opts = {}) {
    const store = shell();
    if (!store) return undefined;
    if (opts.userChose) store._userChoseActiveSource = true;
    store._autoloadAfterSignInPending = false;
    if (store._autoloadIdleWaitCancel) {
        try {
            store._autoloadIdleWaitCancel();
        } catch {
            /* ignore */
        }
        store._autoloadIdleWaitCancel = null;
    }
}

/**
 * Wait until curriculum mount is idle (or user cancelled / timeout).
 * Restore of account sources/trees already finished before autoload runs.
 */
function waitForCurriculumIdle(store, timeoutMs = 20000) {
    if (!store.state.treeHydrating && !store.state.loading) {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        let settled = false;
        let timer = null;
        let raf = 0;
        const finish = () => {
            if (settled) return;
            settled = true;
            if (timer != null) clearTimeout(timer);
            if (raf) cancelAnimationFrame(raf);
            if (store._autoloadIdleWaitCancel === finish) store._autoloadIdleWaitCancel = null;
            resolve();
        };
        store._autoloadIdleWaitCancel = finish;
        timer = setTimeout(finish, timeoutMs);
        const tick = () => {
            if (settled) return;
            if (
                store._userChoseActiveSource ||
                (!store.state.treeHydrating && !store.state.loading)
            ) {
                finish();
                return;
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
    });
}

export function _restorePersistedAuthSessionAction() {
    const store = shell();
    if (!store) return undefined;

    const persisted = loadPersistedAuthSession();
    if (!persisted?.username) return;
    const credentialKind = resolveSessionCredentialKind(persisted);
    store._authSession = {
        ...persisted,
        credentialKind,
        syncQrDataUrl: '',
    };
    if (!persisted.credentialKind || persisted.credentialKind !== credentialKind) {
        persistAuthSession(store._authSession);
    }
    const finalize = () => {
        void _finalizeSyncLoginSessionAction(persisted.username).catch((e) => {
            console.warn('[arborito] restore session finalize failed', e);
        });
    };
    if (hasGdprNetworkConsent()) {
        queueMicrotask(finalize);
    } else {
        onGdprNetworkConsentGranted(finalize);
    }
}

export async function publishIdentityClaimAfterSignInAction() {
    const store = shell();
    if (!store) return undefined;

    const username = store._authSession?.username;
    if (!username) return false;
    const net = await getConnectedNostr(store);
    if (!net) return false;
    const { did, publicJwk, privateJwk } = await ensureLocalEd25519Identity();
    const record = await buildSignedIdentityClaim({ username, did, publicJwk, privateJwk });
    if (!(await verifyIdentityClaimRecord(record))) return false;
    return net.putIdentityClaim({ username, record });
}

export function _schedulePublishIdentityClaimAfterSignInAction() {
    const store = shell();
    if (!store) return undefined;

    queueMicrotask(() => {
        void publishIdentityClaimAfterSignInAction().catch(() => {});
    });
}

export async function _finalizeSyncLoginSessionAction(name, opts = {}) {
    const store = shell();
    if (!store) return undefined;

    const buildQrInBackground = !!opts.deferNetwork;

    const ensureQr = async () => {
        try {
            let sess = store._authSession;
            const isPassword = resolveSessionCredentialKind(sess) === CREDENTIAL_KIND_PASSWORD;
            if (!isPassword) return;
            sess = ensureRecoveryKeyInSession(sess);
            if (store._authSession && store._authSession.username === name) {
                store._authSession = { ...store._authSession, recoveryKeyPlain: sess.recoveryKeyPlain };
            }
            const payload = await buildRecoveryKitQrPayload({
                username: name,
                password: sess?.syncSecretPlain || '',
                recoveryKey: sess?.recoveryKeyPlain || '',
            });
            const dataUrl = payload ? await qrTextToDataUrl(payload, { size: 320 }) : '';
            if (store._authSession && store._authSession.username === name) {
                store._authSession.syncQrDataUrl = dataUrl || '';
                persistAuthSession(store._authSession);
                notifyIdentityChanged(store);
            }
        } catch {
            /* QR generation is best-effort */
        }
    };

    if (buildQrInBackground) {
        try {
            let sess = store._authSession;
            if (resolveSessionCredentialKind(sess) === CREDENTIAL_KIND_PASSWORD) {
                sess = ensureRecoveryKeyInSession(sess);
                if (store._authSession && store._authSession.username === name) {
                    store._authSession = {
                        ...store._authSession,
                        recoveryKeyPlain: sess.recoveryKeyPlain,
                    };
                    persistAuthSession(store._authSession);
                }
            }
        } catch {
            /* ignore */
        }
        void ensureQr();
    } else {
        await ensureQr();
    }

    const runNetworkRestore = async () => {
        /* Gate publishes BEFORE enabling cloudProgressSync.persist — otherwise
         * onPersist schedules an 800ms publish of empty/pre-pull state. */
        store._nostrProgressPullInFlight = true;
        clearTimeout(store._nostrProgressSyncTimer);
        store._nostrProgressSyncTimer = null;
        try {
            try {
                store.userStore.state.cloudProgressSync = true;
                store.userStore.persist();
            } catch {
                /* ignore */
            }
            _schedulePublishIdentityClaimAfterSignInAction();
            /* Relays must be up before escrow / private-tree queries (F5 race). */
            try {
                await warmNostrRelayConnections(store, { probe: false, timeoutMs: 12000 });
            } catch (e) {
                console.warn('[arborito] nostr warm before restore failed', e);
            }
            await store._restoreOrPublishUserPairEscrow?.(name);
            /* Pull remote profile/progress BEFORE any publish. Publishing first with
             * empty local state was overwriting other devices (emoji, lessons, arcade).
             * Isolate each step so one failure does not skip private garden restore. */
            try {
                await store.loadInstalledSourcesFromAccount?.(name);
            } catch (e) {
                console.warn('[arborito] installed sources restore failed', e);
            }
            /*
             * Push this device's saved network courses (share-code / Install) up to the
             * account after pull — otherwise a course added while briefly offline or
             * before session finalize never reaches other devices.
             */
            try {
                store.publishInstalledSourcesForAccount?.({ immediate: true });
            } catch (e) {
                console.warn('[arborito] installed sources publish after restore failed', e);
            }
            try {
                await store.loadPrivateTreesFromAccount?.(name);
            } catch (e) {
                console.warn('[arborito] private trees restore failed', e);
            }
            try {
                await store.loadOwnedTreesFromDirectory?.(name);
            } catch (e) {
                console.warn('[arborito] owned trees restore failed', e);
            }
            try {
                await store._loadProgressForOwnedTrees?.(name);
            } catch (e) {
                console.warn('[arborito] owned progress restore failed', e);
            }
            try {
                await store._loadProgressForInstalledSources?.();
            } catch (e) {
                console.warn('[arborito] installed progress restore failed', e);
            }
            try {
                await store._loadAccountCareProgress?.();
            } catch (e) {
                console.warn('[arborito] account care restore failed', e);
            }
            /* Register opt-in: push local authored branches after pull (new accounts are empty). */
            if (store._pendingSyncLocalBranchesOnRegister) {
                store._pendingSyncLocalBranchesOnRegister = false;
                try {
                    await store.syncAllLocalPrivateBranchesToAccount?.({ silent: true });
                } catch (e) {
                    console.warn('[arborito] post-register local branch sync failed', e);
                }
            }
        } catch (e) {
            console.warn('Post-login restore failed', e);
            store._pendingSyncLocalBranchesOnRegister = false;
        } finally {
            store._nostrProgressPullInFlight = false;
        }
        try {
            store.ensureInstalledSourcesBackgroundSync?.();
        } catch {
            /* ignore */
        }
        /* Account data is ready — open last-opened once (no blind timers). */
        await autoloadTreeAfterSignInAction(name);
        try {
            void store.reconcileNetworkProgress?.();
        } catch {
            /* ignore */
        }
        notifyIdentityChanged(store);
    };

    /*
     * Auth UX: never hold the sign-in / register spinner on relay restores.
     * QR + session are ready; trees/progress catch up in the background.
     */
    if (opts.deferNetwork) {
        void runNetworkRestore().catch((e) => {
            console.warn('[arborito] deferred post-login restore failed', e);
        });
        notifyIdentityChanged(store);
        return;
    }

    await runNetworkRestore();
}

/**
 * Never leave a blank sky after sign-in (onboarding defers boot while Courses is open).
 * @param {object} store
 */
async function ensureCanvasAfterSignIn(store) {
    /* Even if the user picked a course, an empty canvas still needs the demo. */
    if (store.state.data) return;
    /* Real mount still running — do not steal it. Bare hydrating with no worker = failed load. */
    if (
        store.state.treeHydrating &&
        (store._curriculumMountInFlight || store._autoloadMountInFlight || store._ensureDemoMountInFlight)
    ) {
        return;
    }
    try {
        const ok = await store.ensureMinimumDemoMounted?.();
        if (ok) return;
    } catch (e) {
        console.warn('[arborito] sign-in demo remount failed', e);
    }
    try {
        const cur = store.state.modal;
        const ct = typeof cur === 'string' ? cur : cur?.type;
        /* Never replace onboarding — wizard Continue opens Courses. */
        if (!ct) store.setModal({ type: 'sources' });
    } catch (e) {
        console.warn('[arborito] sign-in forest picker open failed', e);
    }
}

/**
 * After account restore: apply "last opened" once, or leave the current tree (incl. demo).
 * Waits only for an in-flight curriculum mount to finish — not for arbitrary delays.
 */
export async function autoloadTreeAfterSignInAction(username) {
    const store = shell();
    if (!store) return undefined;

    const name = String(username || '').trim();
    if (!name) return;

    store._userChoseActiveSource = false;
    store._autoloadAfterSignInPending = true;

    try {
        await waitForCurriculumIdle(store);
        if (store._userChoseActiveSource) return;

        const branches = Array.isArray(store.userStore?.state?.branches)
            ? store.userStore.state.branches
            : [];
        const community = Array.isArray(store.state.communitySources)
            ? store.state.communitySources
            : [];
        const preferredUrl = String(store._restoredActiveSourceUrl || '').trim();
        const active = store.state.activeSource;
        const activeUrl = String(active?.url || '').trim();
        const mountBusy = !!(
            store._curriculumMountInFlight ||
            store._autoloadMountInFlight ||
            store._ensureDemoMountInFlight
        );
        const canvasReady = !!(store.state.data || (store.state.treeHydrating && mountBusy));

        const openLocalById = (localId) => {
            const entry = branches.find((t) => t && t.id === localId);
            if (!entry) return null;
            return {
                id: entry.id,
                name: entry.name || 'Tree',
                url: `branch://${entry.id}`,
                type: 'branch',
                isTrusted: true,
            };
        };

        const openComposedById = (treeId) => {
            const entry = store.userStore?.getTree?.(treeId);
            if (!entry) return null;
            return {
                id: entry.id,
                treeId: entry.id,
                name: entry.name || 'Tree',
                url: `tree://${entry.id}`,
                type: 'composed-tree',
                isTrusted: true,
            };
        };

        let src = null;

        if (preferredUrl) {
            if (preferredUrl.startsWith('branch://')) {
                src = openLocalById(preferredUrl.slice('branch://'.length).split('/')[0]);
            } else if (preferredUrl.startsWith('privtree://')) {
                src = openLocalById(preferredUrl.slice('privtree://'.length).split('/')[0]);
            } else if (preferredUrl.startsWith('tree://')) {
                src = openComposedById(preferredUrl.slice('tree://'.length).split('/')[0]);
            } else {
                src = findCommunitySourceByUrl(community, preferredUrl);
            }
            if (
                src &&
                active &&
                canvasReady &&
                canonicalTreeKey(activeUrl) === canonicalTreeKey(src.url || preferredUrl)
            ) {
                store._restoredActiveSourceUrl = '';
                return;
            }
            /* Preferred missing locally, or same pointer with a blank canvas → fall through. */
            if (!src) store._restoredActiveSourceUrl = '';
        }

        if (!src) {
            /* No usable last-opened: keep a painted tree; otherwise pick local/community. */
            if (active && canvasReady) return;
            const sorted = [...branches]
                .filter((t) => t?.id && String(t.id) !== DEMO_BRANCH_ID)
                .sort((a, b) => Number(b?.updated || 0) - Number(a?.updated || 0));
            const target = sorted[0];
            if (target?.id) {
                src = {
                    id: target.id,
                    name: target.name || 'Tree',
                    url: `branch://${target.id}`,
                    type: 'branch',
                    isTrusted: true,
                };
            }
            if (!src) {
                const saved = community.filter(
                    (s) => s && s.url && !String(s.url).startsWith('privtree://')
                );
                if (saved.length) src = saved[0];
            }
        }

        if (store._userChoseActiveSource) return;

        if (!src) {
            await ensureCanvasAfterSignIn(store);
            return;
        }

        const cur = store.state.modal;
        const ct = typeof cur === 'string' ? cur : cur?.type;
        if (ct === 'sources' && isSourcesWelcomeLoadClose()) {
            dismissModalAction({ returnToMore: false });
        }

        store._autoloadMountInFlight = true;
        try {
            /* Blank canvas after onboarding: force a real hydrate (soft open paints nothing). */
            const force = !store.state.data;
            const ok = await loadDataAction(src, force);
            store._restoredActiveSourceUrl = '';
            if (
                !ok &&
                !store.state.data &&
                !(
                    store.state.treeHydrating &&
                    (store._curriculumMountInFlight || store._ensureDemoMountInFlight)
                )
            ) {
                await ensureCanvasAfterSignIn(store);
            }
        } finally {
            store._autoloadMountInFlight = false;
        }
    } catch (e) {
        console.warn('[arborito] auto-load tree after sign-in failed', e);
        store._autoloadMountInFlight = false;
        try {
            await ensureCanvasAfterSignIn(store);
        } catch {
            /* ignore */
        }
    } finally {
        store._autoloadAfterSignInPending = false;
        store._autoloadIdleWaitCancel = null;
    }
}

/** Store method alias — prefer awaiting {@link autoloadTreeAfterSignInAction} from finalize. */
export function _scheduleAutoloadTreeAfterSignInAction(username) {
    void autoloadTreeAfterSignInAction(username);
}

/** Store.prototype, explicit actions. */
export const storeIdentityAuthMethods = {
    isSignedIn: isSignedInAction,
    isSyncAccount: isSyncAccountAction,
    signOut: signOutAction,
    cancelAutoloadTreeAfterSignIn: cancelAutoloadTreeAfterSignInAction,
    autoloadTreeAfterSignIn: autoloadTreeAfterSignInAction,
    _restorePersistedAuthSession: _restorePersistedAuthSessionAction,
    publishIdentityClaimAfterSignIn: publishIdentityClaimAfterSignInAction,
    _schedulePublishIdentityClaimAfterSignIn: _schedulePublishIdentityClaimAfterSignInAction,
    _finalizeSyncLoginSession: _finalizeSyncLoginSessionAction,
    _scheduleAutoloadTreeAfterSignIn: _scheduleAutoloadTreeAfterSignInAction,
};
